import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  SxProps,
} from "@mui/material";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowIcon } from "../../../icons/arrowDown.svg";
import { ReactComponent as EditIcon } from "../../../icons/editDark.svg";
import { FormSectionContainer, FormSectionHeader } from "../../styled/styled";

export type FormSectionProps = {
  children: any;
  title?: string | ReactElement | null;
  titleInside?: boolean;
  noBorder?: boolean;
  disablePaddingBottom?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  margin?: boolean;
  saveDisabled?: boolean;
  editDisabled?: boolean;
  edit?: boolean;
  infoTooltip?: ReactElement;
  xsLabel?: number;
  xsField?: number;
  headerSx?: SxProps;
  open?: boolean;
  collapse?: boolean;
  toggleOpen?: () => void;
  customButton?: ReactElement;
  readOnly?: boolean;
};

const FormSection = ({
  children,
  title = "",
  titleInside = false,
  noBorder = false,
  disablePaddingBottom = false,
  onEdit,
  onCancel,
  margin = false,
  saveDisabled = false,
  editDisabled = false,
  edit,
  infoTooltip,
  xsLabel,
  xsField,
  headerSx,
  open,
  collapse,
  toggleOpen,
  customButton,
  readOnly = false,
}: FormSectionProps) => {
  const { t } = useTranslation("common");

  return (
    <FormSectionContainer
      noBorder={noBorder}
      disablePaddingBottom={disablePaddingBottom}
    >
      {!titleInside ? (
        <>
          {title && (
            <Box
              display="flex"
              justifyContent="space-between"
              minHeight="35px"
              marginBottom={margin ? "1rem" : "unset"}
            >
              <Box display="flex" alignItems="center">
                {collapse && (
                  <IconButton
                    sx={{
                      transform: open ? "rotate(180deg)" : "unset",
                    }}
                    onClick={toggleOpen}
                  >
                    <ArrowIcon />
                  </IconButton>
                )}
                <FormSectionHeader
                  onClick={() => {
                    if (toggleOpen) {
                      toggleOpen();
                    }
                  }}
                  sx={{
                    ...headerSx,
                    cursor: toggleOpen ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  fontSize="14px"
                  variant="header"
                >
                  {title}
                </FormSectionHeader>
                {infoTooltip}
              </Box>
              {!readOnly && edit !== undefined && (
                <Box display="flex" gap="1rem">
                  {edit && (
                    <>
                      <Button
                        onClick={onCancel}
                        sx={{ borderRadius: "15px", height: "30px" }}
                        variant="outlined"
                      >
                        {t("common:cancelShort")}
                      </Button>
                      <Button
                        disabled={saveDisabled}
                        sx={{ borderRadius: "15px", height: "30px" }}
                        startIcon={<CheckIcon />}
                        onClick={onEdit}
                        variant="contained"
                      >
                        {t("common:save")}
                      </Button>
                    </>
                  )}
                  {!edit && (
                    <Button
                      disabled={editDisabled}
                      sx={{ borderRadius: "15px", height: "30px" }}
                      startIcon={<EditIcon color="primary" />}
                      onClick={onEdit}
                      variant="outlined"
                    >
                      {t("common:edit")}
                    </Button>
                  )}
                </Box>
              )}
              {!readOnly && !onEdit && customButton ? (
                <Box display="flex" gap="1rem">
                  {customButton}
                </Box>
              ) : null}
            </Box>
          )}

          {collapse ? (
            <Collapse in={open} timeout={100}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                {children}
              </Grid>
            </Collapse>
          ) : (
            <Grid container columnSpacing={2} rowSpacing={1}>
              {children}
            </Grid>
          )}
        </>
      ) : (
        <Grid
          container
          direction="row"
          flexWrap="nowrap"
          alignItems="flex-start"
        >
          <Grid item xs={xsLabel || 4}>
            <FormSectionHeader
              sx={{ ...headerSx }}
              fontSize="14px"
              variant="header"
            >
              {title}
            </FormSectionHeader>
          </Grid>
          <Grid item xs={xsField || 8}>
            {children}
          </Grid>
        </Grid>
      )}
    </FormSectionContainer>
  );
};

export default FormSection;
